import { useShows } from "@/context/shows";
import Container from "../Common/Container";
import { useRouter } from "next/router";
import { encryptInteger } from "@/Utils/encryptInteger";
import { Skeleton } from "@mui/material";
import { useBuy } from "@/context/buy";
import { formatMonthDayYear } from "@/Utils/dateFormat";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";

export default function ShowsListing() {
  const { showsListing } = useShows();
  const { selectedState } = useBuy();
  const router = useRouter();

  const data = Array.isArray(showsListing) ? showsListing : [];

  const slugify = (str) =>
    str
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <Container>
      <div className="py-5 px-6 lg:py-10 lg:pl-[80px] ">
        <motion.p 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-[700] text-xl lg:text-2xl lg:leading-[32px]"
        >
          {selectedState?.name} Shows
        </motion.p>
        <motion.div 
          className="mt-[30px] lg:mt-11 lg:px-12 gap-[38px] grid md:grid-cols-2 lg:gap-11 lg:grid-cols-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {data && data.length === 0 ? (
            <motion.div 
              variants={itemVariants}
              className="no-shows-message text-center col-span-full"
            >
              <p>No Shows Available for this location</p>
            </motion.div>
          ) : (
            data?.map((show, i) => {
              const city = slugify(show?.venue?.city);
              const state = slugify(show?.venue?.state);
              const showName = slugify(show?.name);
              const venueName = slugify(show?.venue?.name);
              const showDate = show?.show_date;

              const fullPath = `/shows/${city}-${state}/${show.id}-for-${showName}-at-${venueName}-on-${showDate}`;
              return (
                <motion.div
                  key={`${show.id}_${i}`}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link
                    href={{
                      pathname: fullPath,
                      query: { id: show?.id },
                    }}
                    as={fullPath}
                    className="cursor-pointer max-w-[380px] lg:aspect-square block"
                  >
                    <img
                      src={show?.banner}
                      className="w-full hidden lg:block h-full rounded-2xl transition-transform duration-300 hover:scale-[1.02]"
                      alt=""
                    />
                    <div className="lg:hidden">
                      <div className="flex flex-col gap-9">
                        <div className="cursor-pointer h-full gap-3 items-start flex">
                          <div className="image rounded-[16px] overflow-hidden w-[40%]">
                            <img
                              className="aspect-square object-cover transition-transform duration-300 hover:scale-[1.02]"
                              src={show.banner}
                              alt=""
                            />
                          </div>
                          <div className="h-full w-[60%]">
                            <div className="flex flex-col justify-between gap-3">
                              <p className="py-1 w-fit px-2 border border-[#28262D] rounded-lg font-[500] text-xs">
                                {show.restriction}
                              </p>
                              <p className="font-[700] text-base line-clamp-1">
                                {show.name}
                              </p>
                              <p className="mt-3 line-clamp-1">
                                <span className="text-xs font-[600] font-urbanist text-white">
                                  {show?.show_date
                                    ? formatMonthDayYear(show.show_date)
                                    : ""}{" "}
                                </span>
                                <span className="text-[#78828A] text-xs font-[500] font-urbanist ml-1">
                                  | {show?.venue?.city}, {show?.venue?.state}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })
          )}
        </motion.div>
      </div>
    </Container>
  );
}
